import { DownOutlined } from "@ant-design/icons";
import {
  faArrowLeft,
  faExclamationTriangle,
  faTrashCan,
  faWarehouse
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { nanoid } from 'nanoid';
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import WarehouseForm from "../../components/WarehouseForm/WarehouseForm";
import Dialog from "../../shared/components/Dialog/Dialog";
import AppModal from "../../shared/components/app-modal/AppModal";
import DialogContent from "../../shared/components/confirmation/Confirmation";
import { Store } from "../../store";
import "./WarehousesList.css";

//AntDesign Imports
import { PlayCircleOutlined, PlusOutlined, VerticalAlignBottomOutlined } from "@ant-design/icons";
import { Button, Dropdown } from "antd";

// API
import Header from "../../components/Header/Header";
import axios from "../../service/axios";

import { toast } from "react-toastify";
import DemoLayout from "../../utils/DemoLayout.json";
import Stack360CompressionUtil from "../../utils/compressionUtil";
import WarehouseLayoutsListPanel from "../WarehouseLayoutsListPanel/WarehouseLayoutsListPanel";

const WarehousesList = () => {
  const {
    state: {},
    dispatch,
  } = useContext(Store);
  const navigate = useNavigate();
  const [dialogToggler, setDialogToggler] = useState(false);

  const [editLayout, setEditLayout] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importLoader, setImportLoader] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [showActivateLayoutModal, setShowActivateLayoutModal] = useState(null);
  const [showDeleteLayoutModal, setShowDeleteLayoutModal] = useState(null);
  const [activeLayout, setActiveLayout] = useState(null);
  const [isDemoLayoutImporting, setIsDemoLayoutImporting] = useState(false);
  const [showEmptyLayoutComponent, setShowEmptyLayoutComponent] = useState(false);

  //get organizationType from local storage
  // const organizationType = localStorage.getItem("organizationType");

  const isMultiMode = JSON.parse(localStorage.getItem("multiMode"));

  // const shouldShowBackButton  = ['Carrier','Shipper'].includes(organizationType);

  const handleBack = () => {
    sendMessage({ type: "NAVIGATE_BACK_STACKENABLE" });
  };

  const backButtonElement = <FontAwesomeIcon icon={faArrowLeft} className="back-arrow" onClick={handleBack} />;

  const backButtonHandler = isMultiMode && { backHandler: backButtonElement };

  const onNavigateToDataView = (warehouse) => {
    const { id, name } = warehouse;
    localStorage.setItem("layoutName", name);
    // now add layoutid to url
    navigate(`/visualizer-dataview?layoutId=${id}`, {
      state: { warehouseLayoutId: id },
    });
  };

  const onNavigateToLayoutEditor = (warehouse) => {
    const { id, name } = warehouse;
    setWarehouse(name);
    dispatch({ type: "SHOW_GLOBAL_LOADER", payload: { isActive: true, loaderText: "Initializing 2D View" } });
    // now add layoutid to url
    navigate(`/layout-editor?layoutId=${id}`, {
      state: { warehouseLayoutId: id },
    });
  };

  const onNavigateToInventory = (warehouse) => {
    const { id, name } = warehouse;
    setWarehouse(name);
    dispatch({ type: "SHOW_GLOBAL_LOADER", payload: { isActive: true, loaderText: "Rendering 3D Warehouse" } });
    navigate(`/warehouse-inventory?layoutId=${id}`, {
      state: { warehouseLayoutId: id },
    });
  };

  const setWarehouse = (name) => {
    const warehouseName = name;
    localStorage.setItem("layoutName", name);
    dispatch({ type: "SET_WAREHOUSE", payload: warehouseName });
  };

  const onDialogHandler = () => {
    setDialogToggler(!dialogToggler);
  };

  const onEditLayoutHandler = (updatedWarehouse) => {
    setEditLayout(!editLayout);

    if(updatedWarehouse) {
      // update the particular warehouse in the list
      const updatedWarehouses = warehouses.map((wh) => wh.id === updatedWarehouse.id ? updatedWarehouse : wh);
      setWarehouses(updatedWarehouses);
      // if the updated warehouse is the active layout, update it too
      if(activeLayout?.id === updatedWarehouse.id) {
        setActiveLayout(updatedWarehouse);
      }
    }
  };

  useEffect(() => {
    if(warehouses.length === 0) {      
      setShowEmptyLayoutComponent(true);
    } else {
      setShowEmptyLayoutComponent(false);
    }
    localStorage.removeItem("layoutName");
    localStorage.removeItem("viewportMode");
  }, [warehouses]);

  const fetchWarehouses = async () => {
    try {
      const { data } = await axios.get("warehouse-layout");
      const warehouses = data?.data;
      setWarehouses(warehouses);
      setActiveLayout(warehouses?.find((warehouse) => warehouse?.isActive) || null);
      dispatch({ type: "CLEAR_ELEMENTS", payload: warehouses || [] });
      dispatch({ type: "SET_WAREHOUSES", payload: warehouses || [] });
    } catch (error) {
      const {
        response: {
          data: { message },
        },
      } = error;
      toast.error(message);
      console.log("error", error);
    } finally {
      dispatch({ type: "HIDE_GLOBAL_LOADER" });
      setLoading(false);
      setIsDemoLayoutImporting(false);
    }
  };

  const onDeleteWarehouseHandler = async () => {
    try {
      const { data } = await axios.delete(`warehouse-layout/${selectedWarehouse?.id}`);
      toast.success("Warehouse deleted successfully");
      setShowDeleteLayoutModal(false);
      fetchWarehouses();
    } catch (error) {
      const {
        response: {
          data: { message },
        },
      } = error;
      const defaultErrorMessage = "Oops Something went wrong";
      toast.error(message || defaultErrorMessage);
      console.log("error", error);
    }
  };

  const onDelete = async (item) => {
    setShowDeleteLayoutModal(!showDeleteLayoutModal);
    setSelectedWarehouse(item);
  };
  const onActivate = async (item) => {
    setShowActivateLayoutModal(!showActivateLayoutModal);
    setSelectedWarehouse(item);
  };
  const onActivateSubmit = async () => {
    try {
      const data = await axios.post(`warehouse-layout/${selectedWarehouse?.id}/activate/`);
      toast.success("Layout activated successfully");
      fetchWarehouses();
      setShowActivateLayoutModal(false);
    } catch (error) {
      const {
        response: {
          data: { message },
        },
      } = error;
      toast.error(message);
      console.log("error", error);
    }
  };  
  
  const onEditLayout = async (item) => {
    setEditLayout(!editLayout);
    setSelectedWarehouse(item);
  };

  useEffect(() => {
    // setLoading(true);
    fetchWarehouses();
  }, []);

  const handleImport = () => {
    // open a file picker
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", ".json,.stack360");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];

      if (!file) return;

      try {
        setImportLoader(true);

        // Validate file before upload
        const validation = await Stack360CompressionUtil.validateFile(file);

        if (!validation.isValid) {
          toast.error(validation.error);
          return;
        }

        const { fileInfo } = validation;

        // Show file info for compressed files
        if (fileInfo.type === "stack360") {
          const sizeInfo = Stack360CompressionUtil.formatFileSize(fileInfo.fileSize);
          const compressionInfo = fileInfo.compressionRatio ? ` (${fileInfo.compressionRatio} compression)` : "";
          toast.info(`Uploading compressed file: ${sizeInfo}${compressionInfo}`);
        }

        const formData = new FormData();
        formData.append("file", file);

        const { data } = await axios.post("warehouse-layout/import/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        // Enhanced success message with import stats
        let successMessage = data.message || "Layout imported successfully";
        if (data.stats) {
          const { objectCount, binCount, subAreaCount } = data.stats;
          successMessage += ` (${objectCount} objects, ${binCount} bins, ${subAreaCount} sub-areas)`;
        }

        toast.success(successMessage);
        fetchWarehouses();

        // Show metadata for compressed files
        if (data.metadata && fileInfo.type === "stack360") {
          console.log("Imported file metadata:", data.metadata);
        }
      } catch (error) {
        let errorMessage = "Import failed";

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
        console.log("Import error:", error);
      } finally {
        setImportLoader(false);
      }
    };
  };

  const loadDemoLayout = async () => {
    try {
      setIsDemoLayoutImporting(true);
      const formData = new FormData();
      // Create a Blob/File from your JSON data
      const demoLayout = DemoLayout
      demoLayout.layout = {
        ...demoLayout.layout,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isActive: false,
        code: nanoid(8)
      }

      const jsonFile = new Blob([JSON.stringify(demoLayout)], {
        type: "application/json",
      });
      formData.append("file", jsonFile);
      const { data } = await axios.post("warehouse-layout/import/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Layout imported successfully");
      fetchWarehouses();
    } catch (error) {
      console.log("error =>", error);
    } finally {
      setIsDemoLayoutImporting(false);
    }
  };

  const items = [
    {
      key: "1",
      label: "Create Layout",
      icon: <PlusOutlined style={{ fontSize: "14px" }} />,
      onClick: () => onDialogHandler(),
    },
    {
      key: "2",
      label: "Import Layout",
      icon: <VerticalAlignBottomOutlined style={{ fontSize: "14px" }} />,
      onClick: handleImport,
      disabled: importLoader,
    },
    {
      key: "3",
      label: "Load Demo Layout",
      icon: <PlayCircleOutlined style={{ fontSize: "14px" }} />,
      onClick: loadDemoLayout,
      disabled: importLoader,
    },
  ];

  useEffect(() => {
    const handleNavigation = (event) => {
      //console.log('handleNavigation PostMessage =>',event);
    };
    window.addEventListener("message", handleNavigation);
  }, []);

  const sendMessage = (message) => {
    const referrer = document.referrer;
    // const parentOrigin = referrer ? new URL(referrer).origin : '*';
    // Send message to parent (WMS application)
    window.parent.postMessage(message, "*");
  };




  return (
    <div className="warehouseList-container">
      <Header title={"Digital Warehouse"} {...backButtonHandler} />
      <div className="warehouseList-wrapper">
        {/* Show banner when no Layout is selected */}
        {!activeLayout && warehouses?.length !== 0 && (
          <div className="activate-layout-banner">
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 8, color: "#8a6d3b" }} />
            <span>It looks like you haven't activated any layout, Please activate it by checking More Actions!</span>
          </div>
        )}

        {/* warehouses layout List */}

        <div className="warehouses-list">
          <div className="warehouse-header">
            <div className="title">Your Layouts</div>
            <div className="actions-btn">
              <Dropdown menu={{ items }} style={{ border: "2px solid black" }}>
                <Button >
                  Actions
                  <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>
          <WarehouseLayoutsListPanel
            loading={loading}
            warehouses={warehouses}
            onDialogHandler={onDialogHandler}
            loadDemoLayout={loadDemoLayout}
            isDemoLayoutImporting={isDemoLayoutImporting}
            onNavigateToLayoutEditor={onNavigateToLayoutEditor}
            onNavigateToInventory={onNavigateToInventory}
            onNavigateToDataView={onNavigateToDataView}
            onEditLayout={onEditLayout}
            onActivate={onActivate}
            onDelete={onDelete}
          />
          
        </div>
      </div>

      {dialogToggler && (
        <Dialog
          title={"Create Layout"}
          children={<WarehouseForm onDialogHandler={onDialogHandler} fetchWarehouses={fetchWarehouses} />}
          onDialogHandler={onDialogHandler}
        />
      )}

      <div className="warehouseList-container">
        {editLayout && (
          <Dialog
            title={"Edit Layout"}
            children={<WarehouseForm warehouseData={selectedWarehouse} fetchWarehouses={fetchWarehouses} onDialogHandler={onEditLayoutHandler} />}
            onDialogHandler={onEditLayoutHandler}
          />
        )}
      </div>
      <AppModal
        width={400}
        isModalActive={showActivateLayoutModal}
        onHandleModalClose={() => setShowActivateLayoutModal(false)}
        onHandleModalSubmit={onActivateSubmit}
        showCancelLabel={true}
        showConfirmLabel={true}
        confirmLabel={"Activate"}
      >
        <DialogContent icon={faWarehouse} subText={"Are you sure you want to activate this layout?"} title={"Activate Layout"} isSuccessIcon={true} />
      </AppModal>
      <AppModal
        width={400}
        isModalActive={showDeleteLayoutModal}
        onHandleModalClose={() => setShowDeleteLayoutModal(false)}
        onHandleModalSubmit={onDeleteWarehouseHandler}
        showCancelLabel={true}
        showConfirmLabel={true}
        confirmLabel={"Yes, Delete"}
        cancelLabel={"No, Keep it"}
        disableSubmit={selectedWarehouse?.isActive}
      >
        <DialogContent 
          icon={faTrashCan} 
          subText={ selectedWarehouse?.isActive ? "The layout you selected is currently active." : "Once deleted, this layout cannot be recovered."} 
          title={"Delete Layout"} 
          showFooter={false} 
          secondLine={selectedWarehouse?.isActive ? 'To delete it, please make another layout active.' : 'Are you sure you want to proceed?'} />
      </AppModal>
    </div>
  );
};

export default WarehousesList;
